import { WatchHandle } from "vue";

export const useFullScreenStore = defineStore("full-screen", () => {
  let elementRef: Element | null = null;
  const fullScreenEnabled = ref(false);
  const { t } = useI18n();
  const manualFullScreenEnabled = ref(false);
  const mockFullScreenElement = ref<HTMLElement | null>(null);
  let mockFullScreenEnabledParent: HTMLElement | null = null;
  let mockFullScreenEnabledNextSibling: HTMLElement | null = null;

  function onFullScreenChange() {
    const fullscreenElement =
      document.fullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).msFullscreenElement ||
      manualFullScreenEnabled.value ||
      mockFullScreenElement.value ||
      null;

    fullScreenEnabled.value = fullscreenElement != null;
  }

  document.addEventListener("fullscreenchange", onFullScreenChange);
  document.addEventListener("webkitfullscreenchange", onFullScreenChange);
  watch(mockFullScreenElement, onFullScreenChange);

  async function setElement(element: Element) {
    elementRef = element;
  }

  async function exitFullScreen() {
    if (mockFullScreenElement.value) {
      exitMockFullScreen();
      return;
    }
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else {
        manualFullScreenEnabled.value = false;
        fullScreenEnabled.value = false;
      }
    } catch (err) {
      if (
        !(err as Error).message
          .toLowerCase()
          .includes("pending operation cancelled by")
      ) {
        throw err;
      }
    }
  }

  const { escape } = useMagicKeys();
  let watchEscapeHandle: WatchHandle | null = null;

  function enterMockFullScreen(e: HTMLElement) {
    mockFullScreenElement.value = e;
    mockFullScreenEnabledParent = e.parentElement;
    mockFullScreenEnabledNextSibling = (e.nextSibling as HTMLElement) || null;
    document.body.appendChild(e);
    e.classList.add("mock-full-screen");

    watchEscapeHandle = watch(escape, exitMockFullScreen);
  }

  function exitMockFullScreen() {
    if (!mockFullScreenElement.value) {
      return;
    }
    if (watchEscapeHandle) {
      watchEscapeHandle();
      watchEscapeHandle = null;
    }
    mockFullScreenElement.value.classList.remove("mock-full-screen");
    mockFullScreenElement.value = null;
    if (mockFullScreenEnabledParent && mockFullScreenEnabledNextSibling) {
      mockFullScreenEnabledParent.insertBefore(
        elementRef as HTMLElement,
        mockFullScreenEnabledNextSibling,
      );
    } else if (mockFullScreenEnabledParent) {
      mockFullScreenEnabledParent.appendChild(elementRef as HTMLElement);
    }
  }

  async function enterFullScreen(e: Event) {
    if (!elementRef) {
      return;
    }

    try {
      if (elementRef.requestFullscreen) {
        await elementRef.requestFullscreen();
      } else if ((elementRef as any).webkitRequestFullscreen) {
        /* Safari */
        await (elementRef as any).webkitRequestFullscreen();
      } else if ((elementRef as any).msRequestFullscreen) {
        /* IE11 */
        await (elementRef as any)?.msRequestFullscreen();
      } else {
        enterMockFullScreen(elementRef as HTMLElement);
        // throw new Error("Error in going to full screen");
      }
    } catch (e) {
      //toast.errorToast(t("SV.MES_FullScreenError"));
      console.error("Error in going to full screen", e);
      enterMockFullScreen(elementRef as HTMLElement);
      //throw e;
    }
  }

  return {
    fullScreenEnabled,
    enterFullScreen,
    exitFullScreen,
    setElement,
    manualFullScreenEnabled,
  };
});
